import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { IconX } from '@tabler/icons-react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const Sheet = DialogPrimitive.Root;
const SheetTrigger = DialogPrimitive.Trigger;
const SheetClose = DialogPrimitive.Close;
const SheetPortal = DialogPrimitive.Portal;

const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
SheetOverlay.displayName = 'SheetOverlay';

const sheetVariants = cva(
  'fixed z-50 gap-4 bg-white dark:bg-zinc-900 p-6 shadow-xl transition ease-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-200 data-[state=open]:duration-200',
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
        bottom: 'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
        left: 'inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
        right: 'inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm',
      },
    },
    defaultVariants: { side: 'right' },
  }
);

const SWIPE_THRESHOLD = 80;

const SheetContent = React.forwardRef(({ side = 'right', className, children, showClose = true, ...props }, ref) => {
  const isBottom = side === 'bottom';
  const [dragY, setDragY] = React.useState(0);
  const dragStartRef = React.useRef(null);
  const contentRef = React.useRef(null);

  // Merge forwarded ref with internal ref
  React.useImperativeHandle(ref, () => contentRef.current);

  // Reset drag when sheet opens
  React.useEffect(() => {
    setDragY(0);
  }, [props.forceMount]);

  const handleTouchStart = React.useCallback((e) => {
    if (!isBottom) return;
    // Only allow drag from the top 48px (handle area) or if the scroll is at top
    const touch = e.touches[0];
    const el = contentRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const touchY = touch.clientY - rect.top;

    // Allow drag from handle area (top 48px) or if scrollable content is at the top
    const scrollableChild = el.querySelector('[data-sheet-scroll]') || el;
    const isAtScrollTop = scrollableChild.scrollTop <= 0;
    const isInHandleArea = touchY <= 48;

    if (isInHandleArea || isAtScrollTop) {
      dragStartRef.current = { y: touch.clientY, startedInHandle: isInHandleArea };
    }
  }, [isBottom]);

  // Use ref for dragY in touchend to avoid stale closure
  const dragYRef = React.useRef(0);

  const handleTouchMove = React.useCallback((e) => {
    if (!dragStartRef.current) return;
    const touch = e.touches[0];
    const dy = touch.clientY - dragStartRef.current.y;
    // Only allow downward drag
    if (dy > 0) {
      dragYRef.current = dy;
      setDragY(dy);
      // Prevent scroll while swiping down
      if (dy > 10) e.preventDefault();
    } else {
      dragYRef.current = 0;
      setDragY(0);
    }
  }, []);

  const handleTouchEnd = React.useCallback(() => {
    if (!dragStartRef.current) return;
    if (dragYRef.current > SWIPE_THRESHOLD) {
      // Close the sheet
      const closeBtn = contentRef.current?.querySelector('[data-sheet-swipe-close]');
      if (closeBtn) closeBtn.click();
    }
    dragYRef.current = 0;
    setDragY(0);
    dragStartRef.current = null;
  }, []);

  // Attach touchmove with { passive: false } so preventDefault works
  React.useEffect(() => {
    const el = contentRef.current;
    if (!el || !isBottom) return;
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', handleTouchMove);
  }, [handleTouchMove, isBottom]);

  return (
    <SheetPortal>
      <SheetOverlay style={isBottom && dragY > 0 ? { opacity: Math.max(0, 1 - dragY / 300) } : undefined} />
      <DialogPrimitive.Content
        ref={contentRef}
        className={cn(sheetVariants({ side }), className)}
        style={isBottom && dragY > 0 ? { transform: `translateY(${dragY}px)`, transition: 'none' } : undefined}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        {...props}
      >
        {isBottom && (
          <div className="flex justify-center pt-2 pb-1 shrink-0 cursor-grab">
            <div className="w-10 h-1 rounded-full bg-zinc-300 dark:bg-zinc-600" />
          </div>
        )}
        {children}
        {/* Hidden close trigger for swipe dismissal */}
        <DialogPrimitive.Close data-sheet-swipe-close className="hidden" />
        {showClose && (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500">
            <IconX size={16} />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = 'SheetContent';

const SheetHeader = ({ className, ...props }) => (
  <div className={cn('flex flex-col space-y-2 mb-4', className)} {...props} />
);

const SheetFooter = ({ className, ...props }) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6', className)} {...props} />
);

const SheetTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold text-zinc-900 dark:text-zinc-100', className)}
    {...props}
  />
));
SheetTitle.displayName = 'SheetTitle';

const SheetDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-zinc-500 dark:text-zinc-400', className)}
    {...props}
  />
));
SheetDescription.displayName = 'SheetDescription';

export { Sheet, SheetPortal, SheetOverlay, SheetTrigger, SheetClose, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription };
